/* eslint-disable max-lines */
import { SiteSchedule } from '@hakimo-ui/hakimo/types';
import { getWeekdays, splitDailyWindow } from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

export const getUpdatedSchedules = (
  scheduleIndex: number,
  newSchedule: SiteSchedule,
  allSchedules: SiteSchedule[]
) => {
  let finalAllSchedules = [...allSchedules];
  const oldSchedule = allSchedules[scheduleIndex];
  const changedDayIndex = newSchedule.enabledDays.findIndex(
    (day, i) => day !== oldSchedule.enabledDays[i]
  );
  // case where only start time and end time are updated
  if (changedDayIndex === -1) {
    finalAllSchedules[scheduleIndex] = newSchedule;
    return finalAllSchedules;
  }
  const changedDayValue = newSchedule.enabledDays[changedDayIndex];
  if (changedDayValue) {
    finalAllSchedules = finalAllSchedules.map((schedule, i) => {
      if (i !== scheduleIndex) {
        schedule.enabledDays[changedDayIndex] = false;
      }
      return schedule;
    });
  } else {
    finalAllSchedules[0].enabledDays[changedDayIndex] = true;
  }
  finalAllSchedules[scheduleIndex] = newSchedule;
  return finalAllSchedules;
};

export const getUpdatedSchedulesOnDelete = (
  deleteIndex: number,
  allSchedules: SiteSchedule[]
) => {
  const updatedSchedules = [...allSchedules];
  const deletedSchedule = updatedSchedules.splice(deleteIndex, 1)[0];
  allSchedules[0].enabledDays = allSchedules[0].enabledDays.map((day, j) => {
    if (deletedSchedule.enabledDays[j]) {
      day = true;
    }
    return day;
  });
  return updatedSchedules;
};

export const convertToTimeFromSeconds = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');
  return `${formattedHours}:${formattedMinutes}`;
};

const rotateArrayValues = (arrVal: boolean[], direction: number) => {
  if (direction === 1) {
    // Rotate right
    arrVal.unshift(!!arrVal.pop());
  } else if (direction === -1) {
    // Rotate left
    arrVal.push(!!arrVal.shift());
  }
  return arrVal;
};

export function getUpdatedScheduleOnTimezoneChange(
  schedules: SiteSchedule[],
  newTimezone: string,
  prevTimezone: string
) {
  const updatedSchedules = schedules.map((schedule) => {
    let isDayOfWeekChanged = false;
    let dayChangeDiff = 0;
    schedule.ranges = schedule.ranges.map((range) => {
      const { startTime, endTime, order } = range;
      const isPrevTzUTC = prevTimezone.toLowerCase() === 'utc';

      const [startHour, startMinute] = startTime.split(':');
      let prevTzStartDate = isPrevTzUTC
        ? dayjs.utc()
        : dayjs().tz(prevTimezone);
      prevTzStartDate = prevTzStartDate
        .set('hour', +startHour)
        .set('minute', +startMinute);

      const [endHour, endMinute] = endTime.split(':');
      let prevTzEndDate = isPrevTzUTC ? dayjs.utc() : dayjs().tz(prevTimezone);
      prevTzEndDate = prevTzEndDate
        .set('hour', +endHour)
        .set('minute', +endMinute);

      const newTzStartDate = prevTzStartDate.tz(newTimezone);
      const newTzEndDate = prevTzEndDate.tz(newTimezone);
      if (
        !isDayOfWeekChanged &&
        prevTzEndDate.format('d') !== newTzStartDate.format('d')
      ) {
        isDayOfWeekChanged = true;
        dayChangeDiff =
          parseInt(newTzStartDate.format('d')) -
          parseInt(prevTzEndDate.format('d'));
        // it resolves the case if converted day becomes saturday to sunday
        // Sunday is 0 while Saturday is 6. Handling this case
        dayChangeDiff = dayChangeDiff / Math.abs(dayChangeDiff);
      }

      return {
        startTime: newTzStartDate.format('HH:mm'),
        endTime: newTzEndDate.format('HH:mm'),
        order,
      };
    });

    if (isDayOfWeekChanged) {
      const updatedEnabledDays = rotateArrayValues(
        [...schedule.enabledDays],
        dayChangeDiff
      );
      schedule.enabledDays = updatedEnabledDays;
    }

    return schedule;
  });

  return updatedSchedules;
}

export interface DayRange {
  name: string;
  symbol: string;
  ranges: { startTime: number; endTime: number; isOverflow?: boolean }[];
}

export const getWeekRanges = (schedules: SiteSchedule[]) => {
  const weekDays = getWeekdays();
  const allWeekRanges: DayRange[] = weekDays.map((day) => {
    return {
      name: day.name,
      symbol: day.symbol,
      ranges: [],
    };
  });

  schedules.forEach((sched) => {
    const enabledDays = sched.enabledDays;
    for (let i = 0; i < enabledDays.length; i++) {
      if (!enabledDays[i]) {
        continue;
      }

      for (let j = 0; j < sched.ranges.length; j++) {
        const currentRange = sched.ranges[j];
        const segments = splitDailyWindow(
          currentRange.startTime,
          currentRange.endTime
        );
        segments.forEach((segment) => {
          const dayIndex = segment.isOverflow ? (i + 1) % weekDays.length : i;
          allWeekRanges[dayIndex].ranges.push({
            startTime: segment.startSec,
            endTime: segment.endSec,
          });
        });
      }
    }
  });

  return allWeekRanges;
};
